import useDialog from 'hooks/common/useDialog';
import { AppBar, Body, Button, Icon, PageLayout } from 'components/common';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useBottomSheet from 'hooks/common/useBottomSheet';
import {
  DETAIL_MAX,
  DETAIL_MIN,
  INQUIRY_CATEGORIES,
  INQUIRY_FAQ,
  INQUIRY_GROUPS,
  isValidEmail,
  loadRecentCoinOrders,
  submitInquiry,
  isInProgress,
  type CoinOrder,
  type InquiryCategoryId,
} from './inquiryService';

type Step = 'category' | 'faq' | 'form' | 'done';

/** 부른 쪽이 문의 항목을 미리 정해 보낼 수 있다(코인샵 → 코인·결제). 모르는 값은 무시한다. */
function readInitialCategory(state: unknown): InquiryCategoryId | null {
  const wanted = (state as { category?: string } | null)?.category;
  const found = INQUIRY_CATEGORIES.find((item) => item.id === wanted);
  return found ? found.id : null;
}

const PageInquiry: React.FC = () => {
  const { open: openDialog } = useDialog();
  const location = useLocation();
  const [step, setStep] = useState<Step>('category');
  const [category, setCategory] = useState<InquiryCategoryId | null>(() => readInitialCategory(location.state));
  const [detail, setDetail] = useState('');
  // 로그인 계정 이메일이 이미 myinfo 에 있다(login.js 가 프로필에서 넣어둔다).
  // 유저가 답장 주소를 따로 칠 이유가 없다.
  const [email, setEmail] = useState<string>(myinfo?.email ?? '');
  const [sending, setSending] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  // 코인 문의에서만 쓴다. null 은 아직 안 불러온 상태, [] 는 내역이 없는 상태.
  const [orders, setOrders] = useState<CoinOrder[] | null>(null);
  const [pickedOrder, setPickedOrder] = useState<CoinOrder | null>(null);
  // 처리중 안내 팝업은 화면당 한 번만 띄운다.
  const pendingNoticeShown = useRef(false);
  // 펼쳐 둔 묶음(drilldown). null 이면 첫 화면이다.
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const { open: openSheet } = useBottomSheet();

  // 화면에 펴는 건 미지급 건만. 전송 본문에는 orders 전체가 그대로 실린다.
  const visibleOrders = orders?.filter((order) => !order.paid) ?? null;

  const faq = category ? INQUIRY_FAQ[category] : undefined;
  const emailOk = isValidEmail(email);

  // 키보드가 올라와도 페이지 높이는 그대로라 전송 버튼이 키보드 뒤로 숨는다.
  // visualViewport 는 키보드와 사파리 툴바를 뺀 실제 보이는 높이를 준다.
  // 앱은 네이티브가 쏘는 layoutChanged 를 쓰지만 그건 웹에 오지 않고 해제 수단도 없어
  // (Handler.events 에 off 를 쓰는 곳이 없다) 여기서는 표준 이벤트만 쓴다.
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const sync = () => setViewportHeight(viewport.height);
    sync();
    viewport.addEventListener('resize', sync);
    return () => viewport.removeEventListener('resize', sync);
  }, []);

  // 코인 문의는 FAQ 를 읽기 전에 자기 충전 내역부터 본다. 화면에는 아직 지급이
  // 안 끝난 건만 편다 — 이미 받은 건은 물어볼 게 없어 고를 수도 없다.
  // 지급 완료 건도 목록에는 남는다. CS 본문 한 줄(buildOrderLine)이 그걸 세야 한다.
  useEffect(() => {
    if (category !== 'payment' || step !== 'faq' || orders !== null) return;
    let alive = true;
    loadRecentCoinOrders().then((list) => {
      if (!alive) return;
      setOrders(list);
      // 처리중 건이 있으면 가장 최근 것을 미리 골라 둔다(목록은 시각 내림차순).
      // 문의를 그대로 보내도 CS 가 어느 주문 얘기인지 알 수 있다.
      const latest = list.find(isInProgress);
      if (latest) setPickedOrder(latest);
    });
    return () => {
      alive = false;
    };
  }, [category, step, orders]);

  const bodyHeight = viewportHeight
    ? `calc(${viewportHeight}px - var(--status-bar-height) - var(--appbar-height))`
    : 'calc(100dvh - var(--status-bar-height) - var(--appbar-height))';

  const title = useMemo(() => {
    if (step === 'category' && openGroup) {
      const group = INQUIRY_GROUPS.find((item) => item.id === openGroup);
      return group?.titleKey ? brsc[group.titleKey] : brsc.restrictions_contact;
    }
    if (!category || step === 'category' || step === 'done') return brsc.restrictions_contact;
    const selected = INQUIRY_CATEGORIES.find((item) => item.id === category);
    return selected ? brsc[selected.labelKey] : brsc.restrictions_contact;
  }, [category, step, openGroup]);

  /** 뒤로가기는 단계를 하나씩 되돌린 뒤에야 페이지를 닫는다. */
  const handleBack = () => {
    if (step === 'form') {
      setStep(faq ? 'faq' : 'category');
      return;
    }
    if (step === 'faq') {
      setStep('category');
      return;
    }
    if (openGroup) {
      setOpenGroup(null);
      return;
    }
    Transition.back();
  };

  const handleSubmit = async () => {
    if (!category || sending) return;
    setSending(true);
    try {
      await submitInquiry({
        category,
        detail,
        email,
        locale: BResource.getLocale(),
        orders: orders ?? undefined,
        pickedOrder,
      });
      setStep('done');
    } catch (error) {
      BLog.error('[PageInquiry] submit failed:', error);
      void openDialog({ description: brsc.inquiry_send_fail, confirmText: brsc.close });
    } finally {
      setSending(false);
    }
  };

  const canProceed = (() => {
    if (sending) return false;
    if (step === 'category') return !!category;
    if (step === 'form') return detail.trim().length >= DETAIL_MIN && emailOk;
    return true;
  })();

  const footerText = (() => {
    if (step === 'category') return brsc.next;
    if (step === 'faq') return brsc.inquiry_write;
    if (step === 'form') return sending ? brsc.inquiry_sending : brsc.send;
    return brsc.confirm;
  })();

  const handleFooterClick = async () => {
    if (!canProceed) return;
    if (step === 'category') {
      setOpenFaq(0);
      setStep(faq ? 'faq' : 'form');
      return;
    }
    if (step === 'faq') {
      // 아직 지급이 안 끝난 건이 목록에 있으면, 그게 곧 답이다. 문의를 쓰기 전에 한 번 알린다.
      // 두 번은 안 띄운다 — 뒤로 갔다 다시 오는 길목이라 매번 막으면 방해가 된다.
      if (!pendingNoticeShown.current && orders?.some(isInProgress)) {
        pendingNoticeShown.current = true;
        const result = await openSheet('Common', {
          id: 'Common',
          data: {
            title: brsc.inprogress,
            description: brsc.inquiry_order_pending_confirm,
            confirmText: brsc.inquiry_write,
            cancelText: brsc.close,
            showSkipButton: true,
          },
        });
        if (result.data) setStep('form');
        return;
      }
      setStep('form');
      return;
    }
    if (step === 'form') {
      handleSubmit();
      return;
    }
    Transition.back();
  };

  return (
    <PageLayout>
      <AppBar title={title} onBack={handleBack} />
      {/* PageLayout 은 100vh 인데 iOS Safari 의 100vh 는 하단 툴바를 빼지 않아 버튼이 툴바에 가린다.
          앱에서는 --navigation-bar-height 가 그 자리를 잡지만 웹에서는 0px 이라 직접 재야 한다. */}
      <Body className="flex flex-col" style={{ height: bodyHeight }}>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {step === 'category' && openGroup === null && (
            <>
              <div className="typography-headline-lg mb-4">{brsc.inquiry_category_q}</div>
              {INQUIRY_GROUPS.map((group) => {
                // 접어 둔 묶음은 첫 화면에 이름 한 줄만 낸다. 안에서 고른 항목이 있으면 오른쪽에 적는다.
                if ('drilldown' in group && group.drilldown) {
                  const picked = INQUIRY_CATEGORIES.find((item) => item.group === group.id && item.id === category);
                  return (
                    <div key={group.id} className="mb-5">
                      <div
                        className="typography-body-md flex min-h-[56px] items-center overflow-hidden rounded-lg bg-base-bg-subtle px-4 py-3"
                        onClick={() => setOpenGroup(group.id)}
                      >
                        <div className={`flex-1 ${picked ? 'font-bold' : ''}`}>{group.titleKey && brsc[group.titleKey]}</div>
                        {picked && (
                          <div className="typography-body-sm mr-1 text-base-txt-secondary">{brsc[picked.labelKey]}</div>
                        )}
                        <Icon name="icon/ic_arrow_right.svg" size={24} color="filter-base-ic-tertiary" />
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={group.id} className="mb-5">
                    {group.titleKey && (
                      <div className="typography-headline-sm mb-2 text-base-txt-secondary">{brsc[group.titleKey]}</div>
                    )}
                    <div className="overflow-hidden rounded-lg bg-base-bg-subtle">
                      {INQUIRY_CATEGORIES.filter((item) => item.group === group.id).map((item, index) => (
                        <div
                          key={item.id}
                          className={`typography-body-md flex min-h-[56px] items-center px-4 py-3 ${
                            index > 0 ? 'border-0 border-t border-solid border-base-border-default' : ''
                          } ${category === item.id ? 'font-bold' : ''}`}
                          onClick={() => {
                            setCategory(item.id);
                            setPickedOrder(null);
                          }}
                        >
                          <div className="flex-1">{brsc[item.labelKey]}</div>
                          {category === item.id && <Icon name="icon/ic_check.svg" size={24} color="filter-base-ic-purple" />}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {step === 'category' && openGroup !== null && (
            <div className="overflow-hidden rounded-lg bg-base-bg-subtle">
              {INQUIRY_CATEGORIES.filter((item) => item.group === openGroup).map((item, index) => (
                <div
                  key={item.id}
                  className={`typography-body-md flex min-h-[56px] items-center px-4 py-3 ${
                    index > 0 ? 'border-0 border-t border-solid border-base-border-default' : ''
                  } ${category === item.id ? 'font-bold' : ''}`}
                  onClick={() => {
                    setCategory(item.id);
                    setPickedOrder(null);
                  }}
                >
                  <div className="flex-1">{brsc[item.labelKey]}</div>
                  {category === item.id && <Icon name="icon/ic_check.svg" size={24} color="filter-base-ic-purple" />}
                </div>
              ))}
            </div>
          )}

          {step === 'faq' && category === 'payment' && visibleOrders !== null && visibleOrders.length > 0 && (
            <div className="mb-6">
              <div className="typography-headline-lg mb-3">
                {`${brsc.my_coin} ${MyDataManager.getCoin() ?? 0}`}
              </div>
              <div className="overflow-hidden rounded-lg bg-base-bg-subtle">
                {visibleOrders.map((order, index) => (
                  <div
                    key={order.key}
                    className={`flex min-h-[56px] items-center px-4 py-3 ${
                      index > 0 ? 'border-0 border-t border-solid border-base-border-default' : ''
                    }`}
                    // 고르면 그 주문번호가 문의 본문에 실린다. 안 골라도 문의는 그대로 나간다.
                    onClick={() => setPickedOrder(pickedOrder?.key === order.key ? null : order)}
                  >
                    <div className="flex-1">
                      {/* 코인 수는 안 적는다. 아직 못 받은 건이라 적으면 받은 것처럼 읽힌다.
                          Play 가 아직 확정 안 한 건은 실패가 아니라 처리중이다. */}
                      <div className="typography-body-md">
                        {isInProgress(order) ? brsc.inprogress : brsc.coin_charge_failed}
                      </div>
                      <div className="typography-caption-body mt-0.5 text-base-txt-secondary">
                        {`${TimeUtil.getYYMMDDFromDate(new Date(order.at))} ${TimeUtil.getHHMMSSFromDate(
                          new Date(order.at)
                        )}`}
                      </div>
                    </div>
                    {pickedOrder?.key === order.key && (
                      <Icon name="icon/ic_check.svg" size={24} color="filter-base-ic-purple" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'faq' && faq && (
            <>
              <div className="typography-headline-lg mb-3">{brsc.inquiry_faq_head}</div>
              {faq.map((item, index) => (
                <div
                  key={item.qKey}
                  className={`mb-2 rounded-md border border-solid p-4 ${
                    openFaq === index ? 'border-base-border-focus' : 'border-base-border-default'
                  }`}
                  // 열린 문항을 다시 눌러도 닫지 않는다. 증상 FAQ 는 문항이 하나뿐이라
                  // 닫으면 답을 못 본 빈 화면에서 다음 단계로 넘어가게 된다.
                  onClick={() => setOpenFaq(index)}
                >
                  <div className="typography-headline-sm">{brsc[item.qKey]}</div>
                  {openFaq === index && (
                    <div
                      className="typography-caption-body mt-2 text-base-txt-secondary"
                      // 답변 본문은 기존 qna_contents_* 리소스라 HTML 태그를 포함한다.
                      dangerouslySetInnerHTML={{ __html: item.aKeys.map((key) => brsc[key]).filter(Boolean).join('<br>') }}
                    />
                  )}
                </div>
              ))}
            </>
          )}

          {step === 'form' && (
            <>
              <div className="typography-headline-sm mb-2">{brsc.inquiry_detail_label}</div>
              <textarea
                rows={6}
                maxLength={DETAIL_MAX}
                placeholder={brsc.inquiry_detail_hint}
                className="typography-body-sm min-h-[120px] w-full resize-none rounded-md border border-solid border-input-border-default bg-base-bg-default p-3 text-input-txt-default outline-none placeholder:text-input-txt-placeholder focus:border-input-border-focus"
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
              />
              {detail.trim().length > 0 && detail.trim().length < DETAIL_MIN && (
                <div className="typography-caption-body mt-1.5 text-base-txt-secondary">{brsc.inquiry_detail_hint}</div>
              )}

              <div className="typography-headline-sm mb-2 mt-5">{brsc.inquiry_reply_email}</div>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                className="typography-body-sm h-12 w-full rounded-md border border-solid border-input-border-default bg-base-bg-default px-3 text-input-txt-default outline-none placeholder:text-input-txt-placeholder focus:border-input-border-focus"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {email.length > 0 && !emailOk && (
                <div className="typography-caption-body mt-1.5 text-base-txt-red">{brsc.inquiry_email_invalid}</div>
              )}

              <div className="typography-caption-body mt-5 text-base-txt-tertiary">{brsc.inquiry_auto_attached}</div>
            </>
          )}

          {step === 'done' && (
            <div className="flex h-full flex-col items-center justify-center px-6 text-center">
              <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-base-bg-focus">
                <Icon name="icon/ic_check.svg" size={36} color="filter-base-ic-purple" />
              </div>
              <div className="typography-title-md mb-2">{brsc.inquiry_sent_title}</div>
              <div className="typography-body-sm text-base-txt-secondary">{brsc.inquiry_sent_desc.replace('$param', email)}</div>
            </div>
          )}
        </div>

        <div className="px-4 pb-[calc(var(--navigation-bar-height)+16px)] pt-3">
          <Button
            style={{
              text: footerText,
              color: canProceed ? 'btn-bg-primary' : 'btn-bg-disabled',
              fontColor: canProceed ? 'btn-txt-primary' : 'btn-txt-disabled',
              borderRadius: 'md',
            }}
            onClick={handleFooterClick}
          />
        </div>
      </Body>
    </PageLayout>
  );
};

export default PageInquiry;
